import { useActionState, useEffect, useRef, useState } from 'react';

import { BaseFieldProps } from '@/fo/ui/kit/field/base';
import { toastBase } from '@/fo/ui/kit/toast/base';

export type FormErrors = Record<string, string | string[] | undefined>;

export type FormState<T = Record<string, unknown>> = {
  data?: T;
  errors?: FormErrors;
  message?: string;
  status?: 'error' | 'success';
};

export type FormAction<T = Record<string, unknown>> = (
  prevState: FormState<T>,
  payload: FormData
) => Promise<FormState<T>>;

export type FormOptions = {
  successToast?: boolean;
};

export const useForm = (
  formAction: FormAction,
  initialState: FormState = {},
  options: FormOptions = {
    successToast: true
  }
) => {
  const [state, action, pending] = useActionState(formAction, initialState);
  const [errors, setErrors] = useState<typeof state.errors>({});
  const [isDirty, setIsDirty] = useState(false);
  const refState = useRef(state);
  refState.current = state;

  useEffect(() => {
    if (!pending && state?.message && state?.status) {
      const showToast =
        state.status === 'success' ? options?.successToast : true;
      if (showToast) {
        toastBase(state?.message, { status: state.status });
      }
    }
  }, [pending]);

  useEffect(() => {
    setErrors(state?.errors || {});
  }, [state?.errors]);

  const setFieldError = (field: string, value?: string) => {
    if (!field) return;

    const stateValue = refState.current.data?.[field];

    const notChanged =
      typeof value === 'boolean'
        ? value === (Boolean(stateValue) || stateValue === 'on')
        : value === stateValue;

    setErrors((s) => ({
      ...s,
      [field]: notChanged ? s?.[field] : undefined
    }));
  };

  const handleFieldChange = (name: string, value: unknown) => {
    setIsDirty(true);
    setFieldError(name, value as string);
  };

  const getFieldProps = (name: string): BaseFieldProps => {
    const fieldsError: FormErrors = errors || {};
    return {
      name,
      error: !!fieldsError[name],
      helperText: Array.isArray(fieldsError[name])
        ? fieldsError[name][0]
        : fieldsError[name],
      onChange: (value) => handleFieldChange(name, value)
    };
  };

  return {
    state,
    action,
    pending,
    errors,
    isDirty,
    getFieldProps,
    handleFieldChange
  };
};
